<template>
  <div>
    <slot
      :options="options"
      :search="search"
      :setSearch="setSearch"
      :loading="pending"
      :option="option"
    >
    </slot>
  </div>
</template>

<script setup lang="ts">
import { isEqual } from "radash";
import type { IRecord } from "~/scad-shared/types/scad";

defineSlots<{
  default(props: {
    options: any[] | undefined;
    option: any | null;
    search: string;
    setSearch: Function;
    loading: boolean;
  }): any;
}>();

const props = withDefaults(
  defineProps<{
    endpoint: string;
    displayKey?: string;
    valueKey?: string;
    search?: string;
    responseKey?: string;
    currentValue?: any;
    limit?: number;
    filter?: { [key: string]: any };
    tokenData?: { [key: string]: any };
  }>(),
  {
    search: "",
    responseKey: "Results",
    displayKey: "Name",
    valueKey: "Id",
    limit: 40,
    filter: () => ({}),
    tokenData: () => ({}),
  }
);

const search = ref(props.search);
function setSearch(searchValue: string) {
  search.value = searchValue;
}
watch(
  () => props.search,
  (newValue) => {
    search.value = newValue;
  }
);

const filter = ref(props.filter);
watch(
  () => props.filter,
  (newValue, oldValue) => {
    if (!isEqual(newValue, oldValue)) {
      filter.value = newValue;
    }
  }
);

const currentValue = ref(props.currentValue);
watch(
  () => props.currentValue,
  (newValue) => {
    currentValue.value = newValue;
  }
);

function processEndpoint(
  endpoint: string,
  tokenData: { [key: string]: any }
): string {
  const tokenPattern = /\{(\w+)\}/g; //pattern to find field tokens eg {id}, {branchId}, etc

  let replacedString: string = endpoint.replace(
    tokenPattern,
    (match, token) => {
      const replacement = tokenData[token];

      return replacement !== undefined ? replacement : 0;
    }
  );
  return replacedString;
}

const { data, pending, refresh } = useFetch<IRecord>(
  () => processEndpoint(props.endpoint, props.tokenData),
  {
    watch: [search, filter],
    query: { take: props.limit },
    onRequest: ({ request, options }) => {
      const searchQuery: { [key: string]: string } = {};

      if (search.value && search.value !== "") {
        searchQuery[`${props.displayKey}Contains`] = search.value;
      }
      
      const formattedFilter: { [key: string]: string } = {};

      // Format props.filter to handle arrays correctly
      Object.keys(props.filter).forEach(key => {
        const value = props.filter[key];
        if (Array.isArray(value)) {
          formattedFilter[key.endsWith('Id') ? key + 's' : key] = value.join(',');
        } else {
          formattedFilter[key] = value;
        }
      });

      options.query = { ...options.query, ...searchQuery, ...formattedFilter };
    },
  }
);

//The reason we execute this separately from the above is for scenarios of editing
// an existing record, where the selected value is not in the limit of the initial load above
const {
  data: currentValueData,
  pending: currentValuePending,
  refresh: currentValueRefresh,
  status,
} = useFetch<IRecord>(() => processEndpoint(props.endpoint, props.tokenData), {
  //watch: [currentValue], //should not need to watch for changes though
  query: { take: 1 },
  onRequest: ({ request, options }) => {
    const searchQuery: { [key: string]: string } = {};

    if (currentValue) {
      if (Array.isArray(currentValue.value)) {
        searchQuery[props.valueKey + 's'] = currentValue.value.join(','); // Converts the array to a comma-separated string
      } else {
        searchQuery[props.valueKey] = currentValue.value;
      }
    }

    const formattedFilter: { [key: string]: string } = {};

    // Format props.filter to handle arrays correctly
    Object.keys(props.filter).forEach(key => {
      const value = props.filter[key];
      if (Array.isArray(value)) {
        formattedFilter[key.endsWith('Id') ? key + 's' : key] = value.join(',');
      } else {
        formattedFilter[key] = value;
      }
    });

    options.query = { ...options.query, ...searchQuery, ...formattedFilter };
  },
});

function hasOption(optionList: any[], optionValue: any) {
  const matchingOptions = optionList.filter((opt) => {
    return opt.value == optionValue;
  });

  return matchingOptions.length > 0;
}

const options = computed(() => {
  if (!data || status.value !== "success") {
    return [];
  }

  const optionList: any[] = [];

  if (
    data.value &&
    typeof data.value === "object" &&
    props.responseKey in data.value
  ) {
    data.value[props.responseKey].forEach((res: any) => {
      optionList.push({
        name: res[props.displayKey],
        value: res[props.valueKey],
      });
    });
  }

  if (
    currentValueData.value &&
    typeof data.value === "object" &&
    props.responseKey in currentValueData.value &&
    currentValue.value
  ) {
    currentValueData.value[props.responseKey].forEach((res: any) => {
      if (!hasOption(optionList, res[props.valueKey])) {
        optionList.push({
          name: res[props.displayKey],
          value: res[props.valueKey],
        });
      }
    });
  }

  return optionList;
});

const option = computed(() => {
  if (options.value) {
    return options.value[0];
  }
});
</script>

<style scoped></style>
